package com.writerslab

import io.ktor.client.*
import io.ktor.client.plugins.auth.authProviders
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import io.ktor.client.plugins.auth.Auth as KtorAuth

/**
 * Single Ktor client used by the whole frontend.
 *
 * The Bearer auth plugin handles the access-token lifecycle: it reads tokens
 * from AppDataStore, attaches them to every outgoing request, and on a 401
 * response calls `Auth.refreshAccessToken()` to obtain a fresh access token
 * via the backend `/api/auth/refresh` proxy, then retries the request.
 *
 * If refresh fails, AppDataStore is cleared (`Auth.refreshAccessToken` does
 * this) and the original 401 is propagated to the caller — repositories
 * already gracefully degrade to empty/null results in that case.
 */
val httpClient = HttpClient {
    install(ContentNegotiation) {
        json(Json { ignoreUnknownKeys = true })
    }
    install(KtorAuth) {
        bearer {
            loadTokens {
                val access = AppDataStore.getAccessToken() ?: return@loadTokens null
                val refresh = AppDataStore.getRefreshToken().orEmpty()
                BearerTokens(access, refresh)
            }
            refreshTokens {
                val newAccess = Auth.refreshAccessToken() ?: return@refreshTokens null
                val newRefresh = AppDataStore.getRefreshToken().orEmpty()
                BearerTokens(newAccess, newRefresh)
            }
            sendWithoutRequest { true }
        }
    }
}

/**
 * Forces the Bearer plugin to re-read tokens from AppDataStore on the next
 * request. Call this after a fresh login/logout so the plugin doesn't keep
 * using its cached state.
 */
fun resetAuthCache() {
    httpClient.authProviders
        .filterIsInstance<BearerAuthProvider>()
        .firstOrNull()
        ?.clearToken()
}

